import { Link } from 'react-router-dom';
import { ArrowRight, Stars02, X } from '@untitledui/icons';
import { Avatar } from '@/components/base/avatar/avatar';
import { Badge } from '@/components/base/badges/badges';
import { Tooltip, TooltipTrigger } from '@/components/base/tooltip/tooltip';
import type { Match } from '@/entities/match';
import { nameInitials } from '@/features/matches/lib/initials';
import { cn } from '@/shared/lib/cn';

export type MatchCardProps = {
  match: Match;
  onAccept?: () => void;
  onSkip?: () => void;
  onOpen?: () => void;
  isAccepting?: boolean;
  className?: string;
};

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation();

export const MatchCard = ({
  match,
  onAccept,
  onSkip,
  onOpen,
  isAccepting,
  className,
}: MatchCardProps) => {
  const { participante, score, altoPotencial, status } = match;
  const tagsBusca = participante.tagsBusca ?? [];
  const tagsOferta = participante.tagsOferta ?? [];
  const visibleBusca = tagsBusca.slice(0, 1);
  const visibleOferta = tagsOferta.slice(0, 1);
  const extraTags = tagsBusca.length + tagsOferta.length - visibleBusca.length - visibleOferta.length;
  const isInConversation = status === 'ACEITO' || status === 'CONVERSANDO';

  return (
    <article
      role="button"
      tabIndex={0}
      aria-label={`Abrir perfil de ${participante.nome}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen?.();
        }
      }}
      className={cn(
        'group flex cursor-pointer flex-col gap-3 rounded-3xl border border-gray-200 bg-white p-4 transition-colors hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black sm:flex-row sm:items-start sm:gap-4',
        className,
      )}
    >
      <Avatar
        size="lg"
        initials={nameInitials(participante.nome)}
        src={participante.fotoUrl ?? undefined}
        alt={participante.nome}
      />

      <div className="flex min-w-0 flex-1 flex-col gap-1.5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate text-base font-semibold text-gray-900">
              {participante.nome}
            </h3>
            <p className="truncate text-sm text-gray-500">
              {[participante.cargo, participante.empresa].filter(Boolean).join(' · ')}
            </p>
          </div>
          <Tooltip
            title={`Score ${score}`}
            description="Calculado a partir de alinhamento entre busca/oferta, região e segmento."
          >
            <TooltipTrigger onClick={stop}>
              <Badge
                size="md"
                color={altoPotencial ? 'success' : 'gray'}
                type={altoPotencial ? 'pill-color' : 'color'}
              >
                {altoPotencial ? (
                  <span className="inline-flex items-center gap-1">
                    <Stars02 className="size-3" aria-hidden="true" />
                    {score}
                  </span>
                ) : (
                  <span>{score}</span>
                )}
              </Badge>
            </TooltipTrigger>
          </Tooltip>
        </div>

        {participante.bio ? (
          <p className="line-clamp-1 text-sm text-gray-600">{participante.bio}</p>
        ) : null}

        {(visibleBusca.length > 0 || visibleOferta.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            {visibleBusca.map((t) => (
              <Badge key={`b-${t.id}`} size="sm" color="blue" type="color">
                Busca: {t.nome}
              </Badge>
            ))}
            {visibleOferta.map((t) => (
              <Badge key={`o-${t.id}`} size="sm" color="purple" type="color">
                Oferta: {t.nome}
              </Badge>
            ))}
            {extraTags > 0 && (
              <Badge size="sm" color="gray" type="color">
                +{extraTags}
              </Badge>
            )}
          </div>
        )}

        {isInConversation ? (
          <div className="mt-2 flex items-center justify-between gap-2">
            <Badge size="sm" color="success" type="pill-color">
              Em conversa
            </Badge>
            <Link
              to={`/app/chat/${match.id}`}
              onClick={stop}
              className="inline-flex items-center gap-1 text-sm font-semibold text-gray-900 underline-offset-4 hover:underline"
            >
              Ir para o chat
              <ArrowRight className="size-4" aria-hidden="true" />
            </Link>
          </div>
        ) : (
          <div className="mt-2 flex items-center justify-end gap-2">
            {onSkip ? (
              <button
                type="button"
                onClick={(e) => {
                  stop(e);
                  onSkip();
                }}
                aria-label={`Pular ${participante.nome}`}
                className="inline-flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-gray-600 hover:bg-gray-100"
              >
                <X className="size-4" aria-hidden="true" />
                Pular
              </button>
            ) : null}
            {onAccept ? (
              <button
                type="button"
                onClick={(e) => {
                  stop(e);
                  onAccept();
                }}
                disabled={isAccepting}
                className="inline-flex h-10 items-center gap-1.5 rounded-full bg-black px-4 text-sm font-semibold text-white transition hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isAccepting ? 'Enviando…' : 'Quero conversar'}
              </button>
            ) : null}
          </div>
        )}
      </div>
    </article>
  );
};
